"use client";

import { useState } from "react";
import AnimateHeight from "react-animate-height";
import { Minus, Plus } from "lucide-react";

import { cn } from "@/lib/format";
import type { TocEntry } from "@/lib/toc";

type Props = { items: TocEntry[]; className?: string };

function outlineNumbers(items: TocEntry[]): string[] {
  let section = 0;
  let subsection = 0;

  return items.map((item) => {
    if (item.level === 2) {
      section += 1;
      subsection = 0;
      return String(section);
    }

    subsection += 1;
    return `${Math.max(section, 1)}.${subsection}`;
  });
}

/** Collapsible article table of contents — closed by default, Plus/Minus
 *  toggle, matching production's TOC card exactly. */
export function TableOfContents({ items, className }: Props) {
  const [open, setOpen] = useState(false);

  if (items.length < 2) return null;

  return (
    <div
      className={cn(
        "relative block rounded-md bg-light p-6 shadow-sm",
        className,
      )}
    >
      {/* A span, not a heading: the TOC label must stay out of the document
          outline so the article's own H2s are the first sections after H1. */}
      <span className="block font-display text-lg font-bold text-headings">
        <button
          type="button"
          className="group flex w-full cursor-pointer items-center gap-x-2 text-left"
          aria-expanded={open}
          aria-controls="toc-panel"
          onClick={() => setOpen((v) => !v)}
        >
          Table of Contents
          <span className="grid size-[22px] shrink-0 place-items-center rounded-full border border-current bg-primary/50 p-0.5 transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-white">
            {open ? <Minus size={14} /> : <Plus size={14} />}
          </span>
        </button>
      </span>

      <AnimateHeight
        id="toc-panel"
        duration={250}
        height={open ? "auto" : 0}
        className="relative [&.rah-static--height-specific]:before:pointer-events-none [&.rah-static--height-specific]:before:absolute [&.rah-static--height-specific]:before:inset-x-0 [&.rah-static--height-specific]:before:bottom-0 [&.rah-static--height-specific]:before:h-12 [&.rah-static--height-specific]:before:bg-gradient-to-t [&.rah-static--height-specific]:before:from-bg [&.rah-static--height-specific]:before:to-bg/0"
      >
        <TocLinks items={items} />
      </AnimateHeight>
    </div>
  );
}

function TocLinks({ items }: { items: TocEntry[] }) {
  const numbers = outlineNumbers(items);

  return (
    <ol className="custom-scroll-bar mt-3 max-h-[300px] space-y-2.5 overflow-y-auto pr-1 text-xs font-medium">
      {items.map((item, itemIndex) => (
        <li key={item.id} className={cn(item.level !== 2 && "pl-4")}>
          <a
            href={`#${item.id}`}
            className="flex items-center gap-1 text-body leading-snug hover:text-primary hover:underline"
          >
            <span className="font-extrabold text-primary">
              {numbers[itemIndex]}
            </span>
            <span className="min-w-0 text-pretty">{item.text}</span>
          </a>
        </li>
      ))}
    </ol>
  );
}
